// @ts-check

/** @file Cross-runtime connectivity notifications for the foreground sync coordinator. */

import {useSyncExternalStore} from "react"

/** @typedef {{remove: () => void}} ProjectSyncAppStateSubscription */
/** @typedef {{addEventListener: (eventName: "change", listener: (state: string) => void) => ProjectSyncAppStateSubscription}} ProjectSyncAppState */
/** @typedef {{addEventListener?: (eventName: string, listener: () => void) => void, removeEventListener?: (eventName: string, listener: () => void) => void}} ProjectSyncBrowserTarget */

/** Notification-only connectivity adapter; SyncClient still checks isOnline before every cycle. */
export class ProjectSyncConnectivity {
  /**
   * Creates an injectable cross-platform foreground connectivity source.
   * @param {{appState?: ProjectSyncAppState | null, browserTarget?: ProjectSyncBrowserTarget}} [options] Platform seams.
   */
  constructor({appState = null, browserTarget = globalThis} = {}) {
    this.appState = appState
    this.browserTarget = browserTarget
  }

  /** @type {Set<(online: boolean) => void>} */
  listeners = new Set()
  /** @type {boolean | null} */
  transportOnline = null
  /** Whether optional browser event listeners are currently owned. */
  browserListenersAttached = false
  /** @type {ProjectSyncAppStateSubscription | null} */
  appStateSubscription = null

  /**
   * Reads transport and platform connectivity without a network probe.
   * @returns {boolean} Whether foreground sync may attempt a request.
   */
  isOnline() {
    if (this.transportOnline === false) return false
    if (typeof navigator !== "undefined" && navigator.onLine === false) return false

    return true
  }

  /**
   * Subscribes to browser connectivity where available and transport-derived
   * connectivity in every runtime.
   * @param {(online: boolean) => void} listener Callback notified after state changes.
   * @returns {() => void} Idempotent unsubscribe.
   * @throws {Error} When the listener contract is invalid.
   */
  subscribe = (listener) => {
    if (typeof listener !== "function") throw new Error("Project sync connectivity listener must be a function")
    this.listeners.add(listener)
    this.attachPlatformListeners()

    return () => {
      this.listeners.delete(listener)
      if (this.listeners.size === 0) this.detachPlatformListeners()
    }
  }

  /**
   * Binds the React Native AppState implementation owned by authenticated app chrome.
   * @param {ProjectSyncAppState} appState React Native application-state source.
   * @returns {() => void} Idempotent release for this exact binding.
   * @throws {Error} When the application-state contract is invalid.
   */
  bindAppState(appState) {
    if (!appState || typeof appState.addEventListener !== "function") {
      throw new Error("Project sync AppState must provide addEventListener")
    }
    if (this.appState !== appState) {
      this.detachAppStateListener()
      this.appState = appState
      this.attachAppStateListener()
    }
    let released = false

    return () => {
      if (released) return
      released = true
      if (this.appState !== appState) return
      this.detachAppStateListener()
      this.appState = null
    }
  }

  /** Marks a completed transport request online. */
  requestSucceeded() {
    this.publish(true)
  }

  /** Marks a network transport failure offline. */
  requestFailed() {
    this.publish(false)
  }

  /** Allows an explicit user retry to probe transport again. */
  retry() {
    this.publish(true)
  }

  /** Attaches browser and native foreground notifications once. */
  attachPlatformListeners() {
    this.attachBrowserListeners()
    this.attachAppStateListener()
  }

  /** Detaches every platform source when the last coordinator stops. */
  detachPlatformListeners() {
    this.detachBrowserListeners()
    this.detachAppStateListener()
  }

  /** Attaches optional browser online/offline events once. */
  attachBrowserListeners() {
    if (this.browserListenersAttached || typeof this.browserTarget.addEventListener !== "function") return
    this.browserListenersAttached = true
    this.browserTarget.addEventListener("online", this.onBrowserOnline)
    this.browserTarget.addEventListener("offline", this.onBrowserOffline)
  }

  /**
   * Detaches optional browser events when the last coordinator stops.
   * @throws {Error} When an attached browser target cannot detach its listeners.
   */
  detachBrowserListeners() {
    if (!this.browserListenersAttached) return
    this.browserListenersAttached = false
    if (typeof this.browserTarget.removeEventListener !== "function") {
      throw new Error("Project sync browser target lost removeEventListener")
    }
    this.browserTarget.removeEventListener("online", this.onBrowserOnline)
    this.browserTarget.removeEventListener("offline", this.onBrowserOffline)
  }

  /**
   * Attaches the native foreground notification while a coordinator listens.
   * @throws {Error} When AppState returns an invalid subscription.
   */
  attachAppStateListener() {
    if (this.appStateSubscription || this.listeners.size === 0 || !this.appState) return
    const subscription = this.appState.addEventListener("change", this.onAppStateChange)

    if (!subscription || typeof subscription.remove !== "function") {
      throw new Error("Project sync AppState subscription must provide remove")
    }
    this.appStateSubscription = subscription
  }

  /** Removes the native foreground notification. */
  detachAppStateListener() {
    if (!this.appStateSubscription) return
    const subscription = this.appStateSubscription

    this.appStateSubscription = null
    subscription.remove()
  }

  /** Publishes the browser's online transition. */
  onBrowserOnline = () => { this.publish(true) }

  /** Publishes the browser's offline transition. */
  onBrowserOffline = () => { this.publish(false) }

  /**
   * Treats each native foreground transition as an explicit transport probe.
   * @param {string} state React Native AppState value.
   */
  onAppStateChange = (state) => {
    if (state !== "active") return
    this.transportOnline = null
    this.notify(this.isOnline())
  }

  /**
   * Publishes a changed connectivity state once.
   * @param {boolean} online New state.
   */
  publish(online) {
    if (this.transportOnline === online) return
    this.transportOnline = online
    this.notify(online)
  }

  /**
   * Notifies every current coordinator without connectivity-state deduplication.
   * @param {boolean} online Connectivity hint.
   */
  notify(online) {
    for (const listener of this.listeners) listener(online)
  }
}

/** Shared app-lifetime connectivity source used by configuration, transport, and UI retry. */
export const projectSyncConnectivity = new ProjectSyncConnectivity()

/**
 * Subscribes UI command gates to the same connectivity source used by SyncClient.
 * @returns {boolean} Whether online-only commands may be attempted.
 */
export function useProjectSyncOnline() {
  return useSyncExternalStore(
    projectSyncConnectivity.subscribe,
    () => projectSyncConnectivity.isOnline(),
    () => projectSyncConnectivity.isOnline()
  )
}
